import {
  cancelPayment,
  fetchTrackingPage,
  setDocCollectorFinished,
  uploadLRS,
} from 'actions';
import {
  buildPayload,
  buildPayloadFromTemplate,
} from 'actions/entities/orders/createOrder';
import { CreditCardPaymentFormContainer } from 'components/CreditCardPaymentFlow/CreditCardPaymentFormContainer';
import { Instructions as OtherInstructions } from 'components/Instructions/Instructions';
import { CreatePlanSuccess } from 'components/RecurringPayments/components/CreatePlanSuccess/CreatePlanSuccess';
import { Spinner } from 'components/ui/Spinner';
import { SENDER_COUNTRY_FIELD } from 'constants/index';
import { useUser } from 'hooks/useUser/useUser';
import { lazy, Suspense, useEffect, useState } from 'react';
import { type ConnectedProps, connect, useSelector } from 'react-redux';
import type { RootState } from 'reducers/types';
import {
  allCollectionFilesUploaded,
  allRequiredFilesUploaded,
  docCollectorFinished,
  documentsCollection,
  getCurrentCurrency,
  getFieldValue,
  getFieldValues,
  getInstructionsError,
  getLRSIdVerificationNeeded,
  getLRSPreviousPayments,
  getPaymentId,
  getSelectedOffer,
  getStudentFullName,
  getStudentID,
  hasLRSInDocumentCollection,
  isBoleto,
  isCreditCardPaymentOrder,
  isDocumentsFetchFailure,
  isLRSPending,
  isOnline,
  isPaymentBankTransfer,
  isSelectedOfferRecurring,
  isStudentPaying,
  requiresDocuments,
  shouldDisplayInstructions,
} from 'selectors';
import {
  isDirectDebitOfferSelected,
  isNonAutomatedRecurring,
} from 'selectors/entities/offers/offers';
import { getOrderTemplate } from 'selectors/orderTemplate/orderTemplate';

type PropsFromRedux = ConnectedProps<typeof connector>;
type InstrucionsProps = PropsFromRedux & {
  goToStep: (step: string) => void;
  onPrev: () => void;
  onClose: () => void;
  onCancel: () => void;
};

const InstructionsComponent = ({
  currency,
  fetchTrackingPage,
  goToStep,
  isCardTokenization,
  isDirectDebitOfferSelected,
  isNonAutomated,
  isSelectedOfferRecurring,
  order,
  selectedOption,
  senderCountry,
  ...otherProps
}: InstrucionsProps) => {
  const [isFetching, setIsFetching] = useState(true);
  const { email: userEmail } = useUser();
  const { sender_email: senderEmail, planId } = useSelector(getFieldValues);

  useEffect(() => {
    async function instructionsSetup() {
      await fetchTrackingPage();
      setIsFetching(false);
    }

    function stopFetching() {
      setIsFetching(false);
    }

    if (isSelectedOfferRecurring || isCardTokenization) {
      stopFetching();
    } else {
      instructionsSetup();
    }
  }, [fetchTrackingPage, isCardTokenization, isSelectedOfferRecurring]);

  if (isFetching) {
    return <Spinner />;
  }

  if (isSelectedOfferRecurring && !isNonAutomated) {
    return isDirectDebitOfferSelected ? (
      <Suspense fallback={<Spinner />}>
        <RecurringDirectDebitForm />
      </Suspense>
    ) : (
      <Suspense fallback={<Spinner />}>
        <RecurringCardForm />
      </Suspense>
    );
  }

  if (isNonAutomated) {
    return (
      <CreatePlanSuccess
        email={userEmail || (senderEmail as string)}
        planId={planId as string}
      />
    );
  }

  return isCardTokenization ? (
    <Suspense fallback={<Spinner />}>
      <CreditCardPaymentFormContainer
        goToStep={goToStep}
        onPrev={otherProps.onPrev}
      />
    </Suspense>
  ) : (
    <OtherInstructions {...otherProps} />
  );
};

const mapStateToProps = (state: RootState) => {
  const orderTemplate = getOrderTemplate(state);
  let orderPayload = {};
  if (orderTemplate.id) {
    orderPayload = buildPayloadFromTemplate(orderTemplate, state);
  } else {
    orderPayload = buildPayload(state);
  }

  const isRecurringSelected = isSelectedOfferRecurring(state);
  const isNonAutomated = isRecurringSelected
    ? isNonAutomatedRecurring(state)
    : false;

  return {
    allRequiredFilesUploaded: allRequiredFilesUploaded(state),
    allCollectionFilesUploaded: allCollectionFilesUploaded(state),
    currency: getCurrentCurrency(state),
    docCollectorFinished: docCollectorFinished(state),
    documentsCollection: documentsCollection(state),
    hasLRSInDocumentCollection: hasLRSInDocumentCollection(state),
    instructionsErrors: getInstructionsError(state),
    isBoleto: isBoleto(state),
    isDocumentsFetchFailure: isDocumentsFetchFailure(state),
    isLRSPending: isLRSPending(state),
    isNonAutomated,
    isDirectDebitOfferSelected: isDirectDebitOfferSelected(state),
    isOfflinePayment: isPaymentBankTransfer(state),
    isOnline: isOnline(state),
    isSelectedOfferRecurring: isRecurringSelected,
    isStudentPaying: isStudentPaying(state),
    lrsIdVerificationNeeded: getLRSIdVerificationNeeded(state),
    order: orderPayload,
    paymentId: getPaymentId(state),
    previousLRSTransactions: getLRSPreviousPayments(state),
    isCardTokenization: isCreditCardPaymentOrder(state),
    requiresDocuments: requiresDocuments(state),
    selectedOption: getSelectedOffer(state),
    senderCountry: getFieldValue(state, SENDER_COUNTRY_FIELD),
    shouldDisplayInstructions: shouldDisplayInstructions(state),
    studentFullName: getStudentFullName(state),
    studentID: getStudentID(state),
  };
};

const mapDispatchToProps = {
  cancelPayment,
  fetchTrackingPage,
  setDocCollectorFinished,
  uploadLRS,
};

// Lazy load components that use internal-react-elements to optimize bundle size
const RecurringDirectDebitForm = lazy(() =>
  import(
    /* webpackChunkName: "recurring-direct-debit" */
    'components/RecurringPayments/DirectDebit/RecurringDirectDebitForm'
  ).then(module => ({
    default: module.RecurringDirectDebitForm,
  })),
);

const RecurringCardForm = lazy(() =>
  import(
    /* webpackChunkName: "recurring-card-form" */
    'components/RecurringPayments/CardForm/RecurringCardForm'
  ).then(module => ({
    default: module.RecurringCardForm,
  })),
);

const connector = connect(mapStateToProps, mapDispatchToProps);
const Instructions = connector(InstructionsComponent);

export { Instructions };
